// Shapes of crm-billing's order and payment operations (internal/billing/orders.go, invoices.go, refunds.go).
import { methodLabel } from "@/lib/payments";

export interface OrderRow {
  id: number;
  keap_id: number | null;
  contact_id: number;
  brand_id: number;
  status: string;
  source: string;
  subtotal_cents: number;
  discount_cents: number;
  shipping_cents: number;
  tax_cents: number;
  total_cents: number;
  promo_code_id: number | null;
  affiliate_id: number | null;
  notes: string | null;
  placed_at: string;
  paid_at: string | null;
  cancelled_at: string | null;
  created_by: string;
}

export interface OrderItem {
  id: number;
  product_id: number | null;
  price_id: number | null;
  description: string;
  quantity: number;
  unit_cents: number;
  discount_cents: number;
  total_cents: number;
}

export interface InvoiceRow {
  id: number;
  number: string;
  status: string;
  kind: string;
  description: string | null;
  amount_due_cents: number;
  amount_paid_cents: number;
  due_date: string | null;
  subscription_id: number | null;
  paid_at: string | null;
  voided_at: string | null;
  void_reason: string | null;
}

export interface PaymentRow {
  id: number;
  contact_id: number;
  order_id: number | null;
  invoice_id: number | null;
  gateway: string;
  method: string | null;
  amount_cents: number;
  refunded_cents: number;
  status: string;
  card_brand: string | null;
  last4: string | null;
  failure_message: string | null;
  attempted_at: string;
  settled_at: string | null;
  source: string | null;
  created_by: string;
  /** apple_pay, google_pay, link, amazon_pay, cashapp (Stripe wallets); null for a typed card. */
  wallet?: string | null;
  /** The payment in Stripe's dashboard (test or live), built by billing. */
  dashboard_url?: string | null;
  /** Stripe's dispute status when the bank opened a chargeback (needs_response, under_review, won, lost…). */
  dispute_status?: string | null;
  disputed_at?: string | null;
  dispute_reason?: string | null;
}

export interface RefundRow {
  id: number;
  payment_id: number;
  amount_cents: number;
  reason: string | null;
  status: string;
  method: string | null;
  failure_message: string | null;
  created_by: string;
  created_at: string;
  dashboard_url?: string | null;
}

export interface OrderDetail extends OrderRow {
  items: OrderItem[];
  invoices: InvoiceRow[];
  payments: PaymentRow[];
  refunds: RefundRow[];
  schedule: { id?: number; due_date: string; amount_cents: number; status: string; invoice_id?: number | null }[];
}

export const ORDER_STATUS: Record<string, [string, "success" | "warning" | "destructive" | "muted" | "default"]> = {
  draft: ["draft", "muted"],
  pending: ["not paid yet", "warning"],
  paid: ["paid", "success"],
  partially_paid: ["partly paid", "warning"],
  refunded: ["refunded", "muted"],
  partially_refunded: ["partly refunded", "muted"],
  cancelled: ["cancelled", "muted"],
  void: ["void", "muted"],
};

export const PAYMENT_STATUS: Record<string, [string, "success" | "warning" | "destructive" | "muted" | "default"]> = {
  pending: ["pending", "warning"],
  succeeded: ["went through", "success"],
  failed: ["declined", "destructive"],
  voided: ["voided", "muted"],
  unknown: ["unknown: check the gateway", "warning"],
};

export const INVOICE_STATUS: Record<string, [string, "success" | "warning" | "destructive" | "muted" | "default"]> = {
  draft: ["draft", "muted"],
  open: ["open", "warning"],
  paid: ["paid", "success"],
  void: ["void", "muted"],
  uncollectible: ["written off", "destructive"],
  awaiting_approval: ["waiting for approval", "warning"],
};

export const ORDER_SOURCE: Record<string, string> = {
  checkout: "website checkout",
  phone: "phone order",
  admin: "entered by staff",
  portal: "customer's account page",
  pay_link: "pay link",
  api: "API",
  mcp: "Claude",
  paypal: "PayPal",
  keap_import: "Keap (imported)",
};

/** "Visa ending 4242", "Apple Pay (Visa ending 4242)", "Link", "PayPal"… (lib/payments). */
export const paidWith = (p: PaymentRow) => methodLabel(p);

/** A disputed (chargeback) payment can't be refunded at Stripe: the bank already took the money back. */
export const inDispute = (p: Pick<PaymentRow, "dispute_status">) => !!p.dispute_status;

/** A refund removes access unless its reason starts with "duplicate" (billing's isDuplicateReason). */
export const isDuplicateReason = (r: string) => r.trim().toLowerCase().startsWith("duplicate");
